import { calendarWorkoutState, localCalendarDay } from '../../utils/calendarWorkoutState';
import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import { workoutStressLabel } from '../../utils/workoutStressLabel';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoShareSocialOutline, IoTrophyOutline } from 'react-icons/io5';
import { workouts, type BriefWorkout, type CalendarWorkout, type WorkoutDetail } from '../../services/api';
import { WorkoutShareModal } from '../../components/WorkoutShareModal';
import { extractStreamRaw, parseStreamData, type StreamPoint } from '../../utils/workoutStream';
import { loadWorkoutWithDetails } from '../workout/workoutDetailLoad';
import { useToast } from '../../context/ToastContext';
import './calendar-workout-share.css';
import { WorkoutStructureBarMini } from '../../components/WorkoutStructureBarMini';
import { WorkoutTypeIconTile } from '../../components/WorkoutTypeIconTile';
import type { ApiStructBlock } from '../../utils/workoutStructure';
import { buildWorkoutRowMetrics } from './calendarHelpers';
import { useLongPress } from '../../hooks/useLongPress';

interface Props {
  workout: CalendarWorkout | (BriefWorkout & Partial<Pick<CalendarWorkout, 'structure' | 'annual_records_count'>>);
  display?: {sport: string; title: string; metrics: string[]};
  onPress: () => void;
  onLongPress?: () => void;
}

/** Shared workout row — calendar day detail + notification inbox. */
export function CalendarWorkoutCard({ workout, onPress, onLongPress, display }: Props) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [sharing, setSharing] = useState(false);
  const [share, setShare] = useState<{ workout: WorkoutDetail; stream: StreamPoint[] } | null>(null);
  const request = useRef(0);
  useEffect(() => () => { request.current++; }, [workout.id]);
  async function openShare() {
    if (sharing || !canShare) return;
    const generation = ++request.current;
    setSharing(true);
    try {
      const detail = await loadWorkoutWithDetails(workout.id);
      let stream: StreamPoint[] = [];
      if (detail.total_time != null && (detail.has_details || detail.fit_file)) {
        try {
          const response = await workouts.getWorkoutData(workout.id);
          stream = parseStreamData(extractStreamRaw(response.data));
        } catch {
          if (generation === request.current) showToast(t('workoutDetail.map.streamFailed'), 'error');
        }
      }
      if (generation === request.current) setShare({ workout: detail, stream });
    } catch {
      if (generation === request.current) showToast(t('workoutDetail.loadFailed'), 'error');
    } finally {
      if (generation === request.current) setSharing(false);
    }
  }
  const sportEnglish = workout.workout_type?.english_name ?? workout.workout_type?.name ?? 'Workout';
  const sportLabel = display?.sport ?? workout.workout_type?.name ?? sportEnglish;
  const { done, missed, rest, canShare } = calendarWorkoutState(workout, localCalendarDay());
  const { bind, wrapClick } = useLongPress(onLongPress ?? (() => {}));

  const metrics = display?.metrics ?? buildWorkoutRowMetrics(workout);
  const tssMetricIndex = metrics.findIndex((metric) => metric.endsWith(' TSS'));

  const structure = workout.structure as { structure?: unknown[] } | null | undefined;

  return (
    <div className={`wo-card-wrap${canShare ? ' wo-card-wrap--shareable' : ''}`}>
    <button type="button" className="wo-card" data-workout-state={rest ? 'rest' : done ? 'done' : missed ? 'missed' : 'pending'} onClick={wrapClick(onPress)} {...bind}>
      <WorkoutTypeIconTile
        typeId={workout.workout_type && 'id' in workout.workout_type ? workout.workout_type.id : undefined}
        name={sportEnglish}
        size="md"
        className="wo-card__sport-icon"
        tone={rest ? 'sport' : done ? 'completed' : 'incomplete'}
      />
      <span className="wo-info">
        <span className="wo-info__meta">{sportLabel}</span>
        <span className="wo-info__title">{missed && <span className="wo-status--missed" title={t('calendar.studio.missed')} aria-label={t('calendar.studio.missed')}>×</span>}{displayDigits(display?.title ?? workout.title ?? sportLabel)}
          {!!workout.annual_records_count && <span className="wo-record-badge" aria-label={t('annualRecords.badge', { count: workout.annual_records_count })}>
            <IoTrophyOutline aria-hidden="true" />{t('annualRecords.shortBadge', { count: workout.annual_records_count, countLabel: displayDigits(String(workout.annual_records_count)) })}
          </span>}</span>
        <span className="wo-info__stats">
          {metrics.map((metric, index) => (
            <span key={`${workout.id}-metric-${index}`} className="ltr-island">
              {index > 0 ? <span className="wo-info__stat-sep" aria-hidden> · </span> : null}
              {index === tssMetricIndex ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                  <b>{displayDigits(metric.replace(/\s*TSS$/, ''))}</b> {workoutStressLabel(workout)}
                </span>
              ) : (
                <b>{displayDigits(metric)}</b>
              )}
            </span>
          ))}
        </span>
        {(structure?.structure?.length ?? 0) > 0 && (
          <span className="wo-info__structure">
            <WorkoutStructureBarMini
              structure={workout.structure as { structure?: ApiStructBlock[] } | null}
            />
          </span>
        )}
      </span>
    </button>
    {canShare && <button type="button" className="wo-card-share" aria-label={t('workoutShare.title')}
      disabled={sharing} aria-busy={sharing} onClick={() => void openShare()}>
      <IoShareSocialOutline aria-hidden="true" />
    </button>}
    {share && <WorkoutShareModal workout={share.workout} stream={share.stream} onClose={() => setShare(null)} />}
    </div>
  );
}
